import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { FullScreenTile } from './FullScreenTile.js';
import { MoreBelow } from './MoreBelow.js';
import { FULL_SCREEN_TILE_GAP } from './TiledLayout.js';
import { tileKey } from './track-references.js';
import { fitPinned, gridBox, tilePositions, tilesBelow } from './fit-tiles.js';
import { scrollDownBy } from './scroll-rows.js';
import { useElementSize } from '../../hooks/useElementSize.js';

/**
 * Room inside the column's scroller for the speaking ring, which is drawn 6px
 * outside a tile (`-inset-1` plus `ring-2`). A scroller clips on both axes,
 * so without it the ring would be cut off at the column's edges.
 */
const RING_ROOM = 6;

/**
 * Padding around the layout: 16px, and 24px from Tailwind's `md` (48rem) up,
 * the same as the tiled layout's `p-4 md:p-6`. It is applied here rather than
 * through classes because the column's `clamp(200px, 20%, 280px)` is
 * measured against the whole area, padding included.
 */
function layoutInset(): number {
  return typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 48rem)').matches
    ? 24
    : 16;
}

interface PinnedLayoutProps {
  /** The pinned tile */
  pinned: TrackReferenceOrPlaceholder;

  /** Everyone else, in the stable order, without the pinned tile */
  others: TrackReferenceOrPlaceholder[];

  /** Subscribed microphone per participant identity, for the speaking ring */
  microphoneTrackRefs: ReadonlyMap<string, TrackReference>;

  /** Pin another tile in place of this one */
  onPin: (key: string) => void;

  /** Back to the tiled layout */
  onUnpin: (key: string) => void;

  /** The `tileKey` whose pin button takes focus when it mounts ({@link FullScreenTile}) */
  focusRequestRef?: React.MutableRefObject<string | null>;
}

/**
 * The full-screen layout with a tile pinned (issue #124, epic D9).
 *
 * The pinned tile is the largest 16:9 box left of a column of 4:3 tiles,
 * centred in its space; the column is `clamp(200px, 20%, 280px)` of the area
 * and scrolls vertically, with a fade and a "N more below" chip
 * ({@link MoreBelow}). Below a 640px area the column becomes a two-column
 * grid under a full-width pinned tile. The geometry is {@link fitPinned}.
 *
 * Like the tiled layout, the area is measured with a `ResizeObserver`, so
 * everything re-fits when the window resizes and when the chat opens or
 * closes; the tiles take their size from `--video-tile-width/height` and are
 * placed absolutely in a flat keyed list, so a re-fit moves them without
 * remounting their video. A camera fills the pinned tile (`object-cover`); a
 * screen share is shown whole (`object-contain`), so it stays readable.
 *
 * Pinning another tile swaps; the pinned tile's Unpin goes back to tiled.
 */
const PinnedLayoutComponent: React.FC<PinnedLayoutProps> = ({
  pinned,
  others,
  microphoneTrackRefs,
  onPin,
  onUnpin,
  focusRequestRef
}) => {
  const areaRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const area = useElementSize(areaRef);
  const count = others.length;
  const inset = layoutInset();

  const layout = useMemo(
    () => fitPinned(count, area.width, area.height, FULL_SCREEN_TILE_GAP, inset),
    [count, area.width, area.height, inset]
  );
  const { stage, others: column, fit } = layout;
  const box = gridBox(fit, FULL_SCREEN_TILE_GAP);
  const positions = useMemo(
    () => tilePositions(count, fit, FULL_SCREEN_TILE_GAP),
    [count, fit]
  );

  const [moreBelow, setMoreBelow] = useState(0);

  const countMoreBelow = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    // Bottom of the visible window, in grid coordinates: the scroller's
    // padding puts the grid RING_ROOM below its top edge, and its box is
    // RING_ROOM taller than the column at each end.
    const visibleBottom = element.scrollTop + column.height + RING_ROOM;
    setMoreBelow(tilesBelow(count, fit, FULL_SCREEN_TILE_GAP, visibleBottom));
  }, [count, fit, column.height]);

  // Re-count on join, leave and re-fit, not only on scroll.
  useLayoutEffect(() => {
    countMoreBelow();
  }, [countMoreBelow]);

  const showNextRow = useCallback(() => {
    const element = scrollRef.current;
    if (element) scrollDownBy(element, fit.height + FULL_SCREEN_TILE_GAP);
  }, [fit.height]);

  const stageStyle = {
    left: stage.left,
    top: stage.top,
    '--video-tile-width': `${stage.width}px`,
    '--video-tile-height': `${stage.height}px`
  } as React.CSSProperties;

  const columnStyle: React.CSSProperties = {
    left: column.left - RING_ROOM,
    top: column.top - RING_ROOM,
    width: column.width + 2 * RING_ROOM,
    height: column.height + 2 * RING_ROOM
  };

  const gridStyle = {
    width: box.width,
    height: box.height,
    '--video-tile-width': `${fit.width}px`,
    '--video-tile-height': `${fit.height}px`
  } as React.CSSProperties;

  return (
    <div
      ref={areaRef}
      data-testid="full-screen-pinned"
      data-arrangement={layout.arrangement}
      className="relative h-full w-full"
    >
      {stage.width > 0 && (
        <>
          <div role="group" aria-label="Pinned" data-testid="pinned-stage" className="absolute" style={stageStyle}>
            <FullScreenTile
              key={tileKey(pinned)}
              trackRef={pinned}
              microphoneTrackRefs={microphoneTrackRefs}
              isPinned
              onUnpin={onUnpin}
              focusRequestRef={focusRequestRef}
            />
          </div>

          {count > 0 && fit.columns > 0 && (
            <div className="absolute" style={columnStyle}>
              <div
                ref={scrollRef}
                onScroll={countMoreBelow}
                data-testid="pinned-others"
                className="h-full w-full overflow-x-hidden overflow-y-auto"
                style={{ padding: RING_ROOM }}
              >
                <div role="list" aria-label="Other participants" className="relative" style={gridStyle}>
                  {others.map((trackRef, index) => (
                    <div
                      key={tileKey(trackRef)}
                      role="listitem"
                      className="absolute"
                      style={{ left: positions[index]?.left ?? 0, top: positions[index]?.top ?? 0 }}
                    >
                      <FullScreenTile
                        trackRef={trackRef}
                        microphoneTrackRefs={microphoneTrackRefs}
                        onPin={onPin}
                        focusRequestRef={focusRequestRef}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <MoreBelow count={moreBelow} onShowMore={showNextRow} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const PinnedLayout = memo(PinnedLayoutComponent);
PinnedLayout.displayName = 'PinnedLayout';
